import { useState } from "react";
import { api, formatApiErrorDetail } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BadgeCheck, Loader2 } from "lucide-react";

// GSTIN box + VERIFY button. Calls the backend GST lookup and hands back the details.
export const GstVerify = ({ value, onChange, onVerified, testId = "gst" }) => {
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(null);

  const verify = async () => {
    const gstin = (value || "").trim().toUpperCase();
    if (gstin.length !== 15) return toast.error("Enter the full 15 character GSTIN");
    setBusy(true);
    try {
      const { data } = await api.get("/gst/verify", { params: { gstin } });
      setOk(data.status || "VERIFIED");
      onChange?.(data.gstin);
      onVerified?.(data);
      if (data.duplicate) {
        toast.warning(
          `Already saved on ${data.duplicate.type} "${data.duplicate.name}" — this would be a duplicate GSTIN`,
          { duration: 8000 }
        );
      }
      toast.success(
        data.source === "appyflow"
          ? `${data.trade_name || data.legal_name} — details filled`
          : data.message || "GSTIN format is valid"
      );
    } catch (err) {
      setOk(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">GSTIN</Label>
        {ok && (
          <span data-testid={`${testId}-verified-badge`}
            className="bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
            {ok}
          </span>
        )}
      </div>
      <div className="mt-2 flex gap-2">
        <Input
          data-testid={`${testId}-gstin-input`}
          className="rounded-sm uppercase"
          maxLength={15}
          value={value || ""}
          onChange={(e) => { setOk(null); onChange?.(e.target.value.toUpperCase()); }}
          placeholder="10AABCU9603R1ZM"
        />
        <Button
          type="button"
          data-testid={`${testId}-verify-btn`}
          variant="outline"
          disabled={busy}
          onClick={verify}
          className="shrink-0 rounded-sm"
        >
          {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <BadgeCheck size={14} className="mr-1.5" />}
          {busy ? "Checking" : "Verify"}
        </Button>
      </div>
    </div>
  );
};
